import { useMemo, useState } from 'react';
import { NutritionDatePicker } from './NutritionDatePicker';
import { PageHeader } from './ui/PageHeader';
import { ExportMenu } from './ui/ExportMenu';
import { EmptyState } from './ui/EmptyState';
import { SkeletonCard } from './ui/Skeleton';
import { SegmentedControl } from './ui/SegmentedControl';
import {
  NUTRITION_ITEMS,
  buildNutritionConsolidatedRows,
  buildNutritionDetailRowsFromReport,
  getActiveNutritionItems,
  NutritionConsolidatedTable,
  NutritionDetailTable,
} from './NutritionTables';

function itemLabel(labels, itemType) {
  if (itemType === 'egg') return labels.nutritionEgg;
  if (itemType === 'banana') return labels.nutritionBanana;
  return labels.nutritionMilk;
}

export function NutritionReportPanel({
  labels,
  report,
  isLoading,
  selectedDate,
  onDateChange,
  availableDates,
  onRefresh,
  exportNutritionData,
  exportNutritionAll,
  onDeleteRecord,
  deleteLoading = false,
  showDelete = false,
  title,
  highlightClassIds = [],
  /** Prefer day consolidation (MDMS totals by class group) as the default. */
  defaultViewMode = 'consolidated',
}) {
  const [viewMode, setViewMode] = useState(defaultViewMode);
  const [exportOpenByItem, setExportOpenByItem] = useState({});
  const [exportAllOpen, setExportAllOpen] = useState(false);

  const isConsolidated = viewMode === 'consolidated';
  const dateIso = report?.date || selectedDate;
  const activeItems = useMemo(
    () => getActiveNutritionItems(report?.classes || []),
    [report?.classes]
  );

  const itemBlocks = useMemo(() => {
    const classes = report?.classes || [];
    const records = report?.records || [];
    return activeItems.map((itemType) => {
      const detailRows = buildNutritionDetailRowsFromReport(classes, itemType, records);
      const consolidatedRows = buildNutritionConsolidatedRows(detailRows);
      return { itemType, detailRows, consolidatedRows };
    });
  }, [activeItems, report?.classes, report?.records]);

  const detailHeaders = useMemo(
    () => [labels.division, labels.boys, labels.girls, labels.totalStudents],
    [labels]
  );

  const consolidatedHeaders = useMemo(
    () => [labels.classNumber, labels.boys, labels.girls, labels.totalStudents],
    [labels]
  );

  const exportAllSections = useMemo(
    () =>
      itemBlocks.map(({ itemType, detailRows, consolidatedRows }) => {
        const rows = isConsolidated ? consolidatedRows : detailRows;
        const headers = isConsolidated ? consolidatedHeaders : detailHeaders;
        const firstCol = isConsolidated ? labels.classNumber : labels.division;
        return {
          itemType,
          sheetName: itemLabel(labels, itemType),
          itemHeader: labels.nutritionItemColumn,
          headers,
          rows: rows.map((row) => ({
            [firstCol]: isConsolidated ? row.group : row.division_name,
            [labels.boys]: row.boys,
            [labels.girls]: row.girls,
            [labels.totalStudents]: row.total,
          })),
        };
      }),
    [itemBlocks, labels, detailHeaders, consolidatedHeaders, isConsolidated]
  );

  if (isLoading && !report) {
    return <SkeletonCard />;
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={title || labels.navNutrition}
        actions={
          <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto">
            {exportNutritionAll && itemBlocks.length > 0 ? (
              <ExportMenu
                open={exportAllOpen}
                onOpenChange={(open) => {
                  if (open) setExportOpenByItem({});
                  setExportAllOpen(open);
                }}
                labels={labels}
                buttonLabel={labels.nutritionExportAll}
                align="right"
                placement="down"
                onExport={(format) =>
                  exportNutritionAll({
                    sections: exportAllSections,
                    fileType: format,
                    dateIso,
                    isConsolidated,
                  })
                }
              />
            ) : null}
            {onRefresh ? (
              <button
                type="button"
                className="text-sm font-medium text-primary hover:underline"
                onClick={onRefresh}
              >
                {labels.refresh}
              </button>
            ) : null}
          </div>
        }
      />

      {onDateChange && (
        <NutritionDatePicker
          dates={availableDates || []}
          selectedDate={selectedDate}
          onSelect={onDateChange}
          labels={labels}
        />
      )}

      {itemBlocks.length > 0 ? (
        <SegmentedControl
          options={[
            { value: 'consolidated', label: labels.reportTodayConsolidated },
            { value: 'detail', label: labels.reportViewByDivision },
          ]}
          value={viewMode}
          onChange={(mode) => {
            setViewMode(mode);
            setExportAllOpen(false);
            setExportOpenByItem({});
          }}
        />
      ) : null}

      {itemBlocks.length === 0 && selectedDate ? (
        <EmptyState title={labels.nutritionNoData} />
      ) : itemBlocks.length > 0 ? (
        itemBlocks.map(({ itemType, detailRows, consolidatedRows }) => {
          const rowsForExport = isConsolidated ? consolidatedRows : detailRows;
          return (
            <section
              key={itemType}
              className="overflow-visible space-y-3 rounded-xl border border-border bg-card p-3 sm:p-4"
            >
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-text">{itemLabel(labels, itemType)}</h3>
                <ExportMenu
                  open={exportOpenByItem[itemType] || false}
                  onOpenChange={(open) => {
                    if (open) {
                      setExportAllOpen(false);
                      setExportOpenByItem({ [itemType]: true });
                    } else {
                      setExportOpenByItem((prev) => ({ ...prev, [itemType]: false }));
                    }
                  }}
                  disabled={!rowsForExport.length}
                  align="right"
                  placement="up"
                  onExport={(format) =>
                    exportNutritionData({
                      itemType,
                      rows: rowsForExport,
                      fileType: format,
                      dateIso,
                      isConsolidated,
                    })
                  }
                  labels={labels}
                />
              </div>
              {isConsolidated ? (
                <NutritionConsolidatedTable rows={consolidatedRows} labels={labels} />
              ) : (
                <NutritionDetailTable
                  rows={detailRows}
                  labels={labels}
                  showDelete={showDelete && !isConsolidated}
                  onDelete={onDeleteRecord}
                  deleteLoading={deleteLoading}
                  highlightClassIds={highlightClassIds}
                />
              )}
            </section>
          );
        })
      ) : null}
    </div>
  );
}

export { NUTRITION_ITEMS };
